/**
 * getStatusBadge — shared order status badge styling helper.
 * Returns Tailwind class strings for the pill, dot, and display label
 * based on a raw order_status string.
 */
export interface StatusBadge {
  /** Tailwind classes for the pill container */
  pill: string;
  /** Tailwind classes for the colored dot */
  dot: string;
  /** Human-readable label */
  label: string;
}

export const getStatusBadge = (rawStatus: string): StatusBadge => {
  const status = (rawStatus || 'PLACED').toUpperCase();
  switch (status) {
    case 'DELIVERED':
    case 'COMPLETED':
    case 'SUCCESS':
      return {
        pill: 'bg-emerald-50 text-emerald-700 border-emerald-200/80',
        dot: 'bg-emerald-500',
        label: 'Delivered',
      };
    case 'CONFIRMED':
    case 'ACCEPTED':
      return {
        pill: 'bg-sky-50 text-sky-700 border-sky-200/80',
        dot: 'bg-[#1AA7DE]',
        label: 'Confirmed',
      };
    case 'SHIPPED':
      return {
        pill: 'bg-indigo-50 text-indigo-700 border-indigo-200/80',
        dot: 'bg-indigo-500',
        label: 'Shipped',
      };
    case 'CANCELLED':
    case 'REJECTED':
    case 'FAILED':
      return {
        pill: 'bg-rose-50 text-rose-700 border-rose-200/80',
        dot: 'bg-rose-500',
        label: 'Cancelled',
      };
    case 'PENDING_CANCELLATION':
      return {
        pill: 'bg-amber-50 text-amber-700 border-amber-200/80',
        dot: 'bg-amber-500',
        label: 'Pending Cancellation',
      };
    case 'PLACED':
      return {
        pill: 'bg-amber-50 text-amber-700 border-amber-200/80',
        dot: 'bg-amber-500',
        label: 'Placed',
      };
    case 'REFUNDED':
      return {
        pill: 'bg-purple-50 text-purple-700 border-purple-200/80',
        dot: 'bg-purple-500',
        label: 'Refunded',
      };
    case 'REFUND_IN_PROGESS':
      return {
        pill: 'bg-yellow-50 text-yellow-800 border-yellow-200/80',
        dot: 'bg-yellow-500',
        label: 'Refund In Progress',
      };
    case 'DISPUTE':
      return {
        pill: 'bg-orange-50 text-orange-700 border-orange-200/80',
        dot: 'bg-orange-500',
        label: 'Dispute',
      };
    case 'RETURNED':
      return {
        pill: 'bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200/80',
        dot: 'bg-fuchsia-500',
        label: 'Returned',
      };
    default:
      return {
        pill: 'bg-slate-100 text-slate-700 border-slate-200/80',
        dot: 'bg-slate-400',
        label: status,
      };
  }
};
